"use client";

/**
 * Excursion planning form — private-excursion CONVERSION form (POST /api/contact).
 *
 * Locale-aware (FR default): all visible strings come from EXCURSION_FORM_UI,
 * the phone validation uses a strict FR pattern in FR and a permissive
 * international pattern in EN (so foreign leads aren't rejected), and the team
 * email subject is flagged "🇬🇧 [EN CLIENT] …" in EN. `locale` is also added to
 * the POST body. The SUBMISSION LOGIC is unchanged — only strings, the phone
 * pattern/title and the subject prefix differ by locale. FR output is
 * byte-for-byte identical to the original.
 *
 * Dependencies: Icon, slug-excursions (excursions list for the dropdown), i18n.
 */
import { useMemo, useState } from "react";
import { Icon } from "@/components/ui/Icon";
import type { Locale } from "@/config/i18n";
import { type I18n, tr } from "@/config/localeText";
import { excursions as excursionsFr } from "../slug-excursions";
import { excursionPagesEn } from "../excursions-en";
import { excursionPagesAr } from "../excursions-ar";
import { withAttribution } from "@/lib/attribution-client";

type ExcursionFormState = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  excursionSlug: string;
  date: string;
  time: string;
  pickup: string;
  passengers: string;
  message: string;
  honeypot: string;
  quoteRequested: boolean;
};

const INITIAL_FORM: ExcursionFormState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  excursionSlug: "",
  date: "",
  time: "",
  pickup: "",
  passengers: "",
  message: "",
  honeypot: "",
  quoteRequested: false,
};

const FR_PHONE = "^(?:(?:\\+|00)33|0)\\s*[1-9](?:[\\s.\\-]*\\d{2}){4}$";
// Permissive international: optional "+" then 7+ digits/separators.
const INTL_PHONE = "^\\+?[0-9][0-9\\s.\\-()]{6,}$";

type ExcursionFormUi = {
  excursions: typeof excursionsFr;
  eyebrow: string;
  heading: string;
  intro: string;
  contactSection: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  phonePattern: string;
  phoneTitle: string;
  detailsSection: string;
  excursionLabel: string;
  excursionPlaceholder: string;
  date: string;
  time: string;
  pickup: string;
  pickupPlaceholder: string;
  passengers: string;
  passengersPlaceholder: string;
  precisionsSection: string;
  messagePlaceholder: string;
  quoteCheckbox: string;
  honeypotLabel: string;
  sending: string;
  submit: string;
  successMessage: string;
  defaultError: string;
  unexpectedError: string;
  subjectPrefix: string;
  // buildMessage strings
  msgExcursionLabel: string;
  msgDate: string;
  msgTime: string;
  msgPickup: string;
  msgPassengers: string;
  msgDetailsHeading: string;
};

const EXCURSION_FORM_UI: I18n<ExcursionFormUi> = {
  fr: {
    excursions: excursionsFr,
    eyebrow: "Demande d'excursion privée",
    heading: "Planifier votre excursion",
    intro:
      "Indiquez la date, l'excursion souhaitée et vos préférences. Notre équipe vous contacte rapidement avec une proposition précise.",
    contactSection: "Vos coordonnées",
    firstName: "Prénom",
    lastName: "Nom",
    email: "Email",
    phone: "Téléphone",
    phonePattern: FR_PHONE,
    phoneTitle: "Format attendu : 06 60 15 98 00 ou +33 6 60 15 98 00",
    detailsSection: "Détails de l'excursion",
    excursionLabel: "Excursion souhaitée",
    excursionPlaceholder: "Excursion souhaitée",
    date: "Date",
    time: "Heure (optionnelle)",
    pickup: "Point de départ",
    pickupPlaceholder: "Hôtel, adresse ou point de rencontre",
    passengers: "Nombre de passagers",
    passengersPlaceholder: "Ex: 2, 4, 8...",
    precisionsSection: "Précisions complémentaires",
    messagePlaceholder:
      "Durée souhaitée, arrêts, préférences véhicule, contraintes spécifiques...",
    quoteCheckbox: "Recevoir un devis détaillé",
    honeypotLabel: "Ne pas remplir",
    sending: "Envoi en cours...",
    submit: "Envoyer ma demande d'excursion",
    successMessage:
      "Votre demande d'excursion est bien envoyée. Nous revenons vers vous rapidement.",
    defaultError: "Impossible d'envoyer votre demande.",
    unexpectedError: "Erreur inattendue, réessayez.",
    subjectPrefix: "Planification excursion - ",
    msgExcursionLabel: "Excursion souhaitée: ",
    msgDate: "Date: ",
    msgTime: "Heure: ",
    msgPickup: "Départ: ",
    msgPassengers: "Passagers: ",
    msgDetailsHeading: "Détails:",
  },
  en: {
    excursions: excursionPagesEn,
    eyebrow: "Private excursion request",
    heading: "Plan your excursion",
    intro:
      "Tell us the date, the excursion you want and your preferences. Our team will get back to you quickly with a precise proposal.",
    contactSection: "Your details",
    firstName: "First name",
    lastName: "Last name",
    email: "Email",
    phone: "Phone",
    phonePattern: INTL_PHONE,
    phoneTitle: "Expected format: +1 555 010 1234 or +33 6 60 15 98 00",
    detailsSection: "Excursion details",
    excursionLabel: "Desired excursion",
    excursionPlaceholder: "Desired excursion",
    date: "Date",
    time: "Time (optional)",
    pickup: "Pick-up point",
    pickupPlaceholder: "Hotel, address or meeting point",
    passengers: "Number of passengers",
    passengersPlaceholder: "e.g. 2, 4, 8...",
    precisionsSection: "Additional details",
    messagePlaceholder:
      "Desired duration, stops, vehicle preferences, specific constraints...",
    quoteCheckbox: "Receive a detailed quote",
    honeypotLabel: "Do not fill in",
    sending: "Sending...",
    submit: "Send my excursion request",
    successMessage:
      "Your excursion request has been sent. We'll get back to you shortly.",
    defaultError: "Couldn't send your request.",
    unexpectedError: "Unexpected error, please try again.",
    subjectPrefix: "🇬🇧 [EN CLIENT] Excursion planning - ",
    msgExcursionLabel: "Desired excursion: ",
    msgDate: "Date: ",
    msgTime: "Time: ",
    msgPickup: "Pick-up: ",
    msgPassengers: "Passengers: ",
    msgDetailsHeading: "Details:",
  },
  ar: {
    excursions: excursionPagesAr,
    eyebrow: "طلب رحلة خاصة",
    heading: "خطّط لرحلتك",
    intro:
      "اذكر التاريخ والرحلة التي ترغب بها وتفضيلاتك. يتواصل معك فريقنا سريعًا بعرض دقيق.",
    contactSection: "بيانات التواصل",
    firstName: "الاسم الأول",
    lastName: "اسم العائلة",
    email: "البريد الإلكتروني",
    phone: "الهاتف",
    phonePattern: INTL_PHONE,
    phoneTitle: "الصيغة المتوقّعة: +1 555 010 1234 أو +33 6 60 15 98 00",
    detailsSection: "تفاصيل الرحلة",
    excursionLabel: "الرحلة المطلوبة",
    excursionPlaceholder: "الرحلة المطلوبة",
    date: "التاريخ",
    time: "الوقت (اختياري)",
    pickup: "نقطة الانطلاق",
    pickupPlaceholder: "الفندق أو العنوان أو نقطة اللقاء",
    passengers: "عدد الركّاب",
    passengersPlaceholder: "مثال: 2، 4، 8…",
    precisionsSection: "تفاصيل إضافية",
    messagePlaceholder:
      "المدة المطلوبة، والتوقّفات، وتفضيلات المركبة، وأي قيود خاصة…",
    quoteCheckbox: "أرغب في الحصول على عرض سعر مفصّل",
    honeypotLabel: "يُرجى عدم التعبئة",
    sending: "جارٍ الإرسال…",
    submit: "أرسل طلب الرحلة",
    successMessage: "تم إرسال طلب رحلتك. سنعاود التواصل معك قريبًا.",
    defaultError: "تعذّر إرسال طلبك.",
    unexpectedError: "خطأ غير متوقّع، يُرجى المحاولة مجددًا.",
    subjectPrefix: "🇸🇦 [AR CLIENT] تخطيط رحلة - ",
    msgExcursionLabel: "الرحلة المطلوبة: ",
    msgDate: "التاريخ: ",
    msgTime: "الوقت: ",
    msgPickup: "الانطلاق: ",
    msgPassengers: "الركّاب: ",
    msgDetailsHeading: "التفاصيل:",
  },
};

const buildMessage = (
  state: ExcursionFormState,
  excursionLabel: string,
  ui: ExcursionFormUi,
): string => {
  const details = [
    `${ui.msgExcursionLabel}${excursionLabel}`,
    `${ui.msgDate}${state.date}`,
    state.time ? `${ui.msgTime}${state.time}` : "",
    state.pickup ? `${ui.msgPickup}${state.pickup}` : "",
    state.passengers ? `${ui.msgPassengers}${state.passengers}` : "",
  ].filter(Boolean);

  return `${state.message}\n\n${ui.msgDetailsHeading}\n- ${details.join("\n- ")}`.trim();
};

export default function ExcursionPlanningForm({
  locale = "fr",
  defaultExcursionSlug,
}: {
  locale?: Locale;
  /** Pré-sélectionne une excursion (utilisé sur une fiche pour pré-remplir). */
  defaultExcursionSlug?: string;
}): JSX.Element {
  const ui = tr(EXCURSION_FORM_UI, locale);
  const [form, setForm] = useState<ExcursionFormState>({
    ...INITIAL_FORM,
    excursionSlug: defaultExcursionSlug ?? "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const excursionOptions = useMemo(
    () =>
      ui.excursions.map((item) => ({
        value: item.slug,
        label: item.hero.title,
      })),
    [ui.excursions],
  );

  const selectedExcursionLabel =
    excursionOptions.find((option) => option.value === form.excursionSlug)
      ?.label || "";

  const onChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onToggleQuote = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, quoteRequested: e.target.checked }));
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setSuccess(null);
    setError(null);

    try {
      const subject = `${ui.subjectPrefix}${selectedExcursionLabel}`;
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        name: `${form.firstName} ${form.lastName}`.trim(),
        email: form.email,
        phone: form.phone,
        subject,
        message: buildMessage(form, selectedExcursionLabel, ui),
        honeypot: form.honeypot,
        formType: "renseignements" as const,
        quoteRequested: form.quoteRequested,
        pickup: form.pickup || undefined,
        dropoff: selectedExcursionLabel || undefined,
        date: form.date,
        time: form.time || undefined,
        passengers: form.passengers || undefined,
        locationType: "excursion_privee",
        locale,
        sourcePage:
          typeof window !== "undefined" ? window.location.href : undefined,
      };

      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(withAttribution(payload)),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || ui.defaultError);
      }

      setSuccess(ui.successMessage);
      setForm(INITIAL_FORM);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : ui.unexpectedError;
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="pb-14 pt-4">
      <div className="rounded-3xl border border-black/15 bg-white p-5 shadow-[0_20px_60px_-35px_rgba(0,0,0,0.25)] sm:p-6 md:p-8">
        <div className="mb-8">
          <p className="mb-3 inline-flex rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.18em] text-primary">
            {ui.eyebrow}
          </p>
          <h2 className="font-space-age text-lg tracking-[0.2em] sm:text-xl">
            {ui.heading}
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-black/75 sm:text-base">
            {ui.intro}
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-6">
          <div className="rounded-2xl border border-black/10 bg-black/[0.02] p-4 sm:p-5">
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.14em] text-black/90">
              {ui.contactSection}
            </h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs text-black/70">
                  {ui.firstName}
                </label>
                <input
                  name="firstName"
                  value={form.firstName}
                  onChange={onChange}
                  required
                  minLength={2}
                  placeholder={ui.firstName}
                  className="min-h-12 w-full rounded-xl border border-black/15 bg-white px-4 text-[15px] text-black outline-none ring-primary/40 transition focus:ring-2"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs text-black/70">
                  {ui.lastName}
                </label>
                <input
                  name="lastName"
                  value={form.lastName}
                  onChange={onChange}
                  required
                  minLength={2}
                  placeholder={ui.lastName}
                  className="min-h-12 w-full rounded-xl border border-black/15 bg-white px-4 text-[15px] text-black outline-none ring-primary/40 transition focus:ring-2"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs text-black/70">
                  {ui.email}
                </label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  required
                  placeholder={ui.email}
                  className="min-h-12 w-full rounded-xl border border-black/15 bg-white px-4 text-[15px] text-black outline-none ring-primary/40 transition focus:ring-2"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs text-black/70">
                  {ui.phone}
                </label>
                <input
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={onChange}
                  required
                  pattern={ui.phonePattern}
                  title={ui.phoneTitle}
                  placeholder={ui.phone}
                  className="min-h-12 w-full rounded-xl border border-black/15 bg-white px-4 text-[15px] text-black outline-none ring-primary/40 transition focus:ring-2"
                />
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-black/10 bg-black/[0.02] p-4 sm:p-5">
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.14em] text-black/90">
              {ui.detailsSection}
            </h3>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-xs text-black/70">
                  {ui.excursionLabel}
                </label>
                <select
                  name="excursionSlug"
                  value={form.excursionSlug}
                  onChange={onChange}
                  required
                  className="min-h-12 w-full rounded-xl border border-black/15 bg-white px-4 text-[15px] text-black outline-none ring-primary/40 transition focus:ring-2"
                >
                  <option value="">{ui.excursionPlaceholder}</option>
                  {excursionOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-xs text-black/70">
                  {ui.date}
                </label>
                <input
                  name="date"
                  type="date"
                  value={form.date}
                  onChange={onChange}
                  required
                  className="min-h-12 w-full rounded-xl border border-black/15 bg-white px-4 text-[15px] text-black outline-none ring-primary/40 transition focus:ring-2"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs text-black/70">
                  {ui.time}
                </label>
                <input
                  name="time"
                  type="time"
                  value={form.time}
                  onChange={onChange}
                  className="min-h-12 w-full rounded-xl border border-black/15 bg-white px-4 text-[15px] text-black outline-none ring-primary/40 transition focus:ring-2"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-xs text-black/70">
                  {ui.pickup}
                </label>
                <input
                  name="pickup"
                  value={form.pickup}
                  onChange={onChange}
                  placeholder={ui.pickupPlaceholder}
                  className="min-h-12 w-full rounded-xl border border-black/15 bg-white px-4 text-[15px] text-black outline-none ring-primary/40 transition focus:ring-2"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-xs text-black/70">
                  {ui.passengers}
                </label>
                <input
                  name="passengers"
                  inputMode="numeric"
                  value={form.passengers}
                  onChange={onChange}
                  placeholder={ui.passengersPlaceholder}
                  className="min-h-12 w-full rounded-xl border border-black/15 bg-white px-4 text-[15px] text-black outline-none ring-primary/40 transition focus:ring-2"
                />
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-black/10 bg-black/[0.02] p-4 sm:p-5">
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.14em] text-black/90">
              {ui.precisionsSection}
            </h3>
            <textarea
              name="message"
              value={form.message}
              onChange={onChange}
              required
              minLength={10}
              rows={5}
              placeholder={ui.messagePlaceholder}
              className="w-full resize-y rounded-xl border border-black/15 bg-white px-4 py-3 text-[15px] text-black outline-none ring-primary/40 transition focus:ring-2"
            />

            <label className="mt-4 inline-flex items-center gap-2 text-sm text-black/85">
              <input
                type="checkbox"
                checked={form.quoteRequested}
                onChange={onToggleQuote}
                className="h-4 w-4 rounded border-black/20 bg-white text-primary focus:ring-primary/40"
              />
              {ui.quoteCheckbox}
            </label>
          </div>

          <div className="hidden" aria-hidden="true">
            <label htmlFor="excursion-honeypot">{ui.honeypotLabel}</label>
            <input
              id="excursion-honeypot"
              name="honeypot"
              tabIndex={-1}
              autoComplete="off"
              value={form.honeypot}
              onChange={onChange}
            />
          </div>

          {success && (
            <p className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-700">
              {success}
            </p>
          )}
          {error && (
            <p className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-700">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 text-[15px] font-semibold text-black transition hover:bg-gold-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            {submitting ? (
              <>
                <Icon icon="mdi:loading" className="h-4 w-4 animate-spin" />
                {ui.sending}
              </>
            ) : (
              ui.submit
            )}
          </button>
        </form>
      </div>
    </section>
  );
}
